import React, { useEffect, useState } from 'react';
import { Sparkles, ChevronDown, Camera, Palette, ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { salesApi, fileUrl } from '../salesApi';
import { useT, fmtDateTime } from '../salesI18n';

const Swatch = ({ hex }) => (hex ? <span className="inline-block h-3 w-3 rounded-full border border-black/10 align-middle mr-1" style={{ background: hex }} /> : null);

const Pref = ({ label, items, testId }) => {
  if (!items?.length) return null;
  return (
    <div data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm text-slate-800">{items.map((i, k) => <span key={i.id || k}>{k > 0 && ', '}<Swatch hex={i.hex} />{i.label}</span>)}</div>
    </div>
  );
};

const openingLine = (o, t) => {
  const bits = [t.openingTypes?.[o.type] || o.type_label];
  if (o.units > 1) bits.push(`${o.units} ${t.studioPlan.units}`);
  if (o.style && o.style !== 'keep') bits.push(t.styles?.[o.style] || t.doorStyles?.[o.style] || o.style_label);
  if (o.grille && o.grille !== 'none') bits.push(t.grilles?.[o.grille] || o.grille_label);
  if (o.color?.name) bits.push(o.color.name);
  if (o.glass && o.glass !== 'clear') bits.push(t.glassTypes?.[o.glass] || o.glass_label);
  if (o.screen && o.screen !== 'none') bits.push(t.screens?.[o.screen] || o.screen_label);
  return bits.filter(Boolean).join(' · ');
};

const summarize = (ops, p) => {
  const rep = ops.filter((o) => o.replace);
  const w = rep.filter((o) => !o.is_door && !o.is_new).length, d = rep.filter((o) => o.is_door && !o.is_new).length, n = rep.filter((o) => o.is_new).length, k = ops.length - rep.length;
  return [w ? `${w} ${p.windows}` : null, d ? `${d} ${p.doors}` : null, n ? `${n} ${p.newOpenings}` : null, k ? `${k} ${p.keep}` : null].filter(Boolean).join(' · ');
};

// Card "Plano do cliente (Studio)" no detalhe do lead: tudo que o cliente fez no PM Studio (fotos, aberturas, simulações, preferências).
export const StudioPlanCard = ({ lead, onOpenSimulation }) => {
  const { t, lang } = useT();
  const p = t.studioPlan;
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(lead.studio_stage === 'estimate_requested');

  useEffect(() => {
    let alive = true;
    salesApi.get(`/leads/${lead.id}/studio-plan`).then((r) => { if (alive) setPlan(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [lead.id, lead.updated_at]);

  const tt = plan?.totals;
  const pref = plan?.preferences || {};
  const chips = tt ? [[tt.photos, p.photos], [tt.openings, p.openings], [tt.windows, p.windows], [tt.doors, p.doors], tt.new_openings ? [tt.new_openings, p.newOpenings] : null, [tt.simulations, p.sims], [tt.images, p.images]].filter(Boolean) : [];

  return (
    <section className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50/80 to-white shadow-sm overflow-hidden" data-testid="studio-plan-card">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid="studio-plan-toggle">
        <span className="h-9 w-9 rounded-lg bg-teal-600 text-white flex items-center justify-center shrink-0"><Sparkles className="h-4 w-4" /></span>
        <span className="flex-1 min-w-0">
          <span className="block font-display font-bold text-slate-900">{p.title}</span>
          <span className="block text-[11px] text-slate-600 truncate">{lead.estimate_requested_at ? `${p.requested} ${fmtDateTime(lead.estimate_requested_at, lang)}` : p.notRequested}{plan?.snapshot_at ? ` · ${p.snapshot} ${fmtDateTime(plan.snapshot_at, lang)}` : ''}</span>
        </span>
        {tt && <span className="hidden sm:flex gap-1" data-testid="studio-plan-chips">{chips.slice(0, 4).map(([n, l]) => <span key={l} className="rounded-full bg-white border border-teal-200 px-2 py-0.5 text-[11px] font-semibold text-teal-800">{n} {l}</span>)}</span>}
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4" data-testid="studio-plan-body">
          {!plan && <p className="text-xs text-muted-foreground">{t.loading}</p>}
          {plan && tt && !tt.photos && !tt.simulations && <p className="text-sm text-muted-foreground" data-testid="studio-plan-empty">{p.empty}</p>}
          {plan && tt && (tt.photos > 0 || tt.simulations > 0) && (
            <>
              <div className="flex flex-wrap gap-1.5 sm:hidden">{chips.map(([n, l]) => <span key={l} className="rounded-full bg-white border border-teal-200 px-2 py-0.5 text-[11px] font-semibold text-teal-800">{n} {l}</span>)}</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-lg bg-white/80 border border-teal-100 p-3" data-testid="studio-plan-prefs">
                <div className="col-span-2 sm:col-span-4 flex items-center gap-1.5 text-xs font-semibold text-teal-800"><Palette className="h-3.5 w-3.5" /> {p.prefs}</div>
                <Pref label={p.style} items={pref.style} testId="studio-plan-pref-style" />
                <Pref label={p.grille} items={pref.grille} testId="studio-plan-pref-grille" />
                <Pref label={p.extColors} items={pref.exterior_colors} testId="studio-plan-pref-ext" />
                <Pref label={p.intColors} items={pref.interior_colors} testId="studio-plan-pref-int" />
                <Pref label={p.glass} items={pref.glass} />
                <Pref label={p.screen} items={pref.screen} />
                <Pref label={p.extTrim} items={pref.exterior_trim} />
                <Pref label={p.intTrim} items={pref.interior_trim} />
              </div>

              {plan.photos.length > 0 && (
                <div className="space-y-2" data-testid="studio-plan-photos">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-800"><Camera className="h-3.5 w-3.5" /> {p.perPhoto}</div>
                  {plan.photos.map((ph) => (
                    <div key={ph.id} className="flex gap-3 rounded-lg bg-white/80 border border-teal-100 p-2" data-testid={`studio-plan-photo-${ph.n}`}>
                      <img src={fileUrl(ph.id, 'thumb')} alt="" className="h-16 w-20 rounded-md object-cover shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-slate-900">{p.photo} {ph.n} · {t[ph.kind] || ph.kind}</div>
                        <div className="text-[11px] text-slate-600">{summarize(ph.openings, p)}</div>
                        <ul className="mt-1 space-y-0.5">
                          {ph.openings.filter((o) => o.replace).map((o) => (
                            <li key={o.n} className="text-[11px] text-slate-700 flex gap-1.5"><span className="font-mono font-semibold text-teal-700 shrink-0">#{o.n}</span><span className="truncate">{o.is_new && <span className="rounded bg-amber-100 text-amber-800 px-1 mr-1 font-bold text-[9px]">{p.new}</span>}{openingLine(o, t)}</span></li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {plan.latest_images.length > 0 && (
                <div className="space-y-2" data-testid="studio-plan-images">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-800"><ImageIcon className="h-3.5 w-3.5" /> {p.latest}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {plan.latest_images.slice(0, 2).map((im, k) => (
                      <React.Fragment key={k}>
                        <figure><img src={fileUrl(im.before_file_id, 'thumb')} alt="" className="w-full aspect-[4/3] rounded-lg object-cover border" /><figcaption className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{p.before}</figcaption></figure>
                        <figure><img src={fileUrl(im.after_file_id, 'thumb')} alt="" className="w-full aspect-[4/3] rounded-lg object-cover border-2 border-amber-400" /><figcaption className="text-[10px] uppercase tracking-wider text-teal-800 font-semibold mt-1 truncate">{p.after} · {[im.color, im.style_label].filter(Boolean).join(' · ')}</figcaption></figure>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {plan && (plan.notes || plan.best_time || plan.year_built) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" data-testid="studio-plan-meta">
              {plan.best_time && <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{p.bestTime}</div>{t.studioTimes?.[plan.best_time] || plan.best_time_label}</div>}
              {plan.year_built && <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{p.yearBuilt}</div>{plan.year_built}</div>}
              {plan.notes && <div className="col-span-2 sm:col-span-1"><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{p.notes}</div><span className="whitespace-pre-wrap">{plan.notes}</span></div>}
            </div>
          )}
          <Button type="button" variant="outline" size="sm" className="h-9 border-teal-300 text-teal-800 hover:bg-teal-50" onClick={onOpenSimulation} data-testid="studio-plan-open-simulation"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> {p.openSim}</Button>
        </div>
      )}
    </section>
  );
};
